import type { ParamSchema } from '@/audio/plugins/types'
import { midiNoteName } from '@/stores/sequencerStore'

/**
 * Generic parameter-form renderer.
 *
 * Takes a plugin's `paramSchema` (shape defined in
 * `audio/plugins/types.ts`) + the current values + an onChange
 * callback, and renders a row of labeled controls. Shapes a
 * plugin can ship a fully-working parameter UI by declaring its
 * schema — no per-plugin React required. Used by the Inspector's
 * Pad / Bus panels for registered instruments and effects.
 *
 * Kind → control mapping:
 *   slider  → <input type="range">
 *   toggle  → checkbox
 *   select  → <select> from `options`
 *   freq    → log-scaled slider (Hz on display)
 *   time    → linear slider in seconds
 *
 * Values are always numbers; `select` options are `[value, label]`
 * tuples so the schema can use either enum-style integers or real
 * continuous values. Anything that can't fit into a number (e.g.
 * the sampler instrument's `buffer`) lives outside the schema and
 * is wired directly by the host.
 */
export interface ParamSchemaFormProps {
  schema: ReadonlyArray<ParamSchema>
  values: Record<string, number | string | undefined>
  onChange: (paramId: string, value: number) => void
  /** Optional section label — rendered as an h4 above the grid. */
  heading?: string
  /** Compact mode — 2 columns, smaller labels; used by the
   *  Inspector's tight space. Default: single column. */
  compact?: boolean
}

export function ParamSchemaForm({
  schema,
  values,
  onChange,
  heading,
  compact = false,
}: ParamSchemaFormProps) {
  if (schema.length === 0) {
    return (
      <p className="text-xs text-content-tertiary italic">
        No parameters declared for this plugin.
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-3">
      {heading && (
        <h4 className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
          {heading}
        </h4>
      )}
      <div className={`grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {schema.map(p => (
          <ParamRow
            key={p.id}
            param={p}
            value={resolveValue(p, values[p.id])}
            onChange={next => onChange(p.id, next)}
            compact={compact}
          />
        ))}
      </div>
    </div>
  )
}

/** Coerce the current value to a number suitable for the control.
 *  Strings, undefined, and non-finite values fall back to the
 *  schema default so the control renders in a sane initial state. */
function resolveValue(
  param: ParamSchema,
  raw: number | string | undefined,
): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  return param.default
}

interface ParamRowProps {
  param: ParamSchema
  value: number
  onChange: (v: number) => void
  compact: boolean
}

function ParamRow({ param, value, onChange, compact }: ParamRowProps) {
  const labelClass = compact
    ? 'text-[9px] font-mono uppercase tracking-[0.1em] text-content-tertiary'
    : 'text-[10px] font-mono uppercase tracking-[0.12em] text-content-tertiary'

  return (
    <label className="flex flex-col gap-1" title={param.help}>
      <div className="flex items-baseline justify-between gap-2">
        <span className={labelClass}>{param.label}</span>
        <span className="text-[11px] font-mono text-content-secondary tabular-nums">
          {formatValue(param, value)}
        </span>
      </div>
      {renderControl(param, value, onChange)}
    </label>
  )
}

function renderControl(
  param: ParamSchema,
  value: number,
  onChange: (v: number) => void,
) {
  if (param.kind === 'toggle') {
    return (
      <input
        type="checkbox"
        checked={value !== 0}
        onChange={e => onChange(e.target.checked ? 1 : 0)}
        className="h-4 w-4 rounded border-brd-subtle bg-surface-sunken accent-[color:var(--accent)]"
      />
    )
  }
  if (param.kind === 'select' && param.options) {
    return (
      <select
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="bg-surface-sunken border border-brd-subtle rounded px-2 py-1 text-xs text-content-primary focus:outline-none focus:ring-1 focus:ring-[color:var(--accent-halo)]"
      >
        {param.options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    )
  }
  const { sliderValue, sliderMin, sliderMax, step } = sliderMapping(
    param,
    value,
  )
  return (
    <input
      type="range"
      min={sliderMin}
      max={sliderMax}
      step={step}
      value={sliderValue}
      onChange={e =>
        onChange(invertSliderMapping(param, Number(e.target.value)))
      }
      className="w-full accent-[color:var(--accent)]"
    />
  )
}

/** Map a schema value to the slider's native coordinate space.
 *  `freq` uses a log mapping so the slider feels musical; `time`
 *  + `slider` are linear. Returns a bundle the renderer can hand
 *  directly to `<input type="range">`. */
function sliderMapping(
  param: ParamSchema,
  value: number,
): {
  sliderValue: number
  sliderMin: number
  sliderMax: number
  step: number
} {
  if (param.kind === 'freq') {
    const min = param.min ?? 20
    const max = param.max ?? 20000
    // Log-scaled slider 0..1000 for consistent feel across the
    // audible range. Invert at `invertSliderMapping`.
    const ratio = Math.log(value / min) / Math.log(max / min)
    return {
      sliderValue: Math.max(0, Math.min(1000, ratio * 1000)),
      sliderMin: 0,
      sliderMax: 1000,
      step: 1,
    }
  }
  const min = param.min ?? 0
  const max = param.max ?? 1
  return {
    sliderValue: value,
    sliderMin: min,
    sliderMax: max,
    step: param.step ?? (param.kind === 'time' ? 0.001 : 0.01),
  }
}

function invertSliderMapping(param: ParamSchema, sliderValue: number): number {
  if (param.kind === 'freq') {
    const min = param.min ?? 20
    const max = param.max ?? 20000
    const ratio = sliderValue / 1000
    return min * Math.pow(max / min, ratio)
  }
  return sliderValue
}

function formatValue(param: ParamSchema, value: number): string {
  if (param.kind === 'toggle') return value !== 0 ? 'on' : 'off'
  if (param.kind === 'select' && param.options) {
    const match = param.options.find(([v]) => v === value)
    return match ? match[1] : String(value)
  }
  if (param.format === 'midi-note') {
    // Musicians read "C3" far faster than "48". Integer-only
    // display — fractional MIDI values round down to the nearest
    // semitone.
    return midiNoteName(Math.round(value))
  }
  if (param.kind === 'freq') {
    if (value >= 1000) return `${(value / 1000).toFixed(2)} kHz`
    return `${Math.round(value)} Hz`
  }
  if (param.kind === 'time') {
    if (value >= 1) return `${value.toFixed(2)} s`
    return `${Math.round(value * 1000)} ms`
  }
  // Plain slider — pick a reasonable precision based on range.
  const range = (param.max ?? 1) - (param.min ?? 0)
  const decimals = range > 100 ? 0 : range > 10 ? 1 : 2
  return value.toFixed(decimals)
}
